import { safeLoader } from "@/lib/safe-loader";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import { createFileRoute } from "@tanstack/react-router";
import { resolveSeo, siteContentQuery, useContent } from "@/lib/site-content";

import { CtaBand, PageHero, Prose } from "../components/site/Page";
import preschoolReading from "../assets/preschool-play.webp";
import preschoolNumbers from "../assets/preschool-numbers.webp";

export const Route = createFileRoute("/forskola")({
  loader: async ({ context }) => {
    const data = await safeLoader("sv.forskola.content", () => context.queryClient.ensureQueryData(siteContentQuery), undefined);
    return { seo: resolveSeo(data?.content, "forskola", "sv") };
  },
  head: ({ loaderData }) => {
    const title = loaderData?.seo.title ?? "";
    const description = loaderData?.seo.description ?? "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        ...ogImage("/og/forskola-v1.jpg"),
        { property: "og:url", content: absoluteUrl("/forskola") },
        { property: "og:locale", content: "sv_SE" },
      ],
      links: seoLinks("/forskola"),
    };
  },
  component: Forskola,
});

function Forskola() {
  const { text } = useContent("forskola");

  return (
    <>
      <PageHero
        eyebrow={text("hero.eyebrow")}
        title={text("hero.title")}
        intro={text("hero.intro")}
      />

      <Prose>
        <img
          src={preschoolReading}
          alt="Pedagog läser en bok tillsammans med barn på förskolan"
          loading="lazy"
          className="rounded-xl shadow-soft"
          width={1264}
          height={848}
        />

        <p>
          I Spanien börjar barn tidigare i åldrarna att träna bokstäver, läsning, skrivning och
          matematik. Vi följer svensk läroplan för förskolan men vill även säkerställa att barnen på
          ett pedagogiskt och lekfullt sätt får möjlighet till detta tidiga lärande på vår förskola.
          På Swedish International School får barnen dessutom redan i förskolan bekanta sig med och
          träna både spanska och engelska, då vi jobbar på ett naturligt sätt med båda språken i
          tillägg till svenskan. Skolan erbjuder två förskolegrupper – en svensk och en internationell
          – när grupperna är tillräckligt stora.
        </p>
        <p>
          Mellan kl 9–15 välkomnas barn från 3 år till en rolig och lärofylld dag i trygg miljö.
        </p>
        <p>
          Förskolan tar emot barn från 3 år. Har ni ett barn som ännu inte fyllt 3 år är ni ändå
          välkomna att höra av er: i undantagsfall, efter en individuell bedömning tillsammans med
          familjen, kan vi erbjuda plats från 2 års ålder. Det är inget vi kan lova på förhand.
        </p>

        <h2>Lek och social utveckling i centrum</h2>
        <p>
          På svenska förskolan vid Swedish International School står leken och den sociala
          utvecklingen i centrum. Pedagogerna fokuserar på att ge barnen möjlighet att utforska och
          upptäcka sin omgivning genom lek och olika aktiviteter. Barnen får också möjlighet att
          utveckla sin språkliga, motoriska och kognitiva förmåga.
        </p>

        <img
          src={preschoolNumbers}
          alt="Barn som arbetar med siffror och material på förskolan"
          loading="lazy"
          className="rounded-xl shadow-soft"
          width={810}
          height={540}
        />

        <h2>Kontinuitet i utbildningen</h2>
        <p>
          En fördel med att gå på en svensk förskola vid Swedish International School är att barnen
          får en kontinuitet i sin utbildning. Förskolan använder svenska som huvudspråk och följer
          den svenska läroplanen, vilket ger eleverna en stabil grund för sin vidare utbildning.
          Barnen får en första introduktion till det svenska skolsystemet och kan lättare anpassa sig
          när de återvänder till Sverige eller fortsätter sin utbildning på andra internationella
          skolor.
        </p>

        <h2>Svenska traditioner och kultur</h2>
        <p>
          Pedagogerna använder sig av sånger, danser och pyssel för att introducera barnen för
          svenska traditioner och högtider. Det ger barnen en möjlighet att lära sig om sitt ursprung
          och känna sig delaktiga i svenska traditioner även om de befinner sig i Spanien.
        </p>
        <p>
          För föräldrar som söker en svenskspråkig utbildning är förskolan ett bra alternativ.
          Förskolan har svensktalande personal och använder svenska som huvudspråk, vilket ger barnen
          möjlighet att behålla och utveckla sin svenska språkförmåga samtidigt som de får en unik
          upplevelse av att växa upp i en annan kultur.
        </p>

        <h2>Dagen på förskolan</h2>
        <p>
          Dagen har en tydlig rytm med samling, aktiviteter i mindre grupper, utevistelse, lunch och
          vila. Barnen är utomhus stora delar av dagen när vädret tillåter, och inomhus finns plats
          för skapande, bygglek, sagostund och lugna stunder. Förskolan är öppen kl 9–15 under
          skoldagar och följer skolans{" "}
          <a className="text-primary link-quiet" href="/lasarstider">läsårstider</a>.
        </p>

        <h2>Språk i vardagen</h2>
        <p>
          Svenskan är basen i verksamheten, samtidigt som spanska och engelska finns med naturligt i
          sånger, lekar och samtal under dagen. För barn som har svenska som andraspråk hemma innebär
          det mycket vardagsspråk tillsammans med vuxna som talar svenska – och för barn som ska bo
          kvar i Spanien en tidig och trygg ingång till spanskan.
        </p>

        <h2>Praktiskt inför start</h2>
        <ul>
          <li>Inskolning planeras tillsammans med er utifrån barnets behov.</li>
          <li>Ta med ombyte, solskydd, keps och vattenflaska märkt med namn.</li>
          <li>Lunch och mellanmål serveras på skolan; specialkost anges i ansökan.</li>
          <li>Sjukfrånvaro anmäls till förskolan på morgonen.</li>
        </ul>

        <h2>Plats och avgifter</h2>
        <p>
          Platser tilldelas löpande och antalet är begränsat, så hör av er i god tid inför önskad
          termin. Avgifter för förskolan finns på{" "}
          <a className="text-primary link-quiet" href="/avgifter">avgiftssidan</a> och ansökan
          gör ni via{" "}
          <a className="text-primary link-quiet" href="/ansokan">anmälningsformuläret</a>. Har ni
          frågor om barnets ålder, grupp eller inskolning är ni välkomna att kontakta oss.
        </p>


      </Prose>
      <FaqBlock items={faqFor("forskola", "sv")} locale="sv" />


      <CtaBand />
      <div className="h-20" />
    </>
  );
}
